import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import AddRounded from "@mui/icons-material/AddRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { asMoney } from "@bw/shared/types";
import { formatMoney } from "@bw/shared/utils";
import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";
import { MembershipPlanAvailabilityControl } from "./membership-plan-availability";

import {
  createMembershipPlan,
  listMembershipPlans,
  updateMembershipPlan,
  type MembershipEntitlementCreate,
  type MembershipPlan,
  type MembershipPlanCreate,
  type MembershipPlanType,
  type MembershipPlanUpdate,
} from "@/libs/facilityApi";

/** Facility-bound event; response/refetch remains the status authority. */
export interface MembershipPlanAvailabilityChange {
  facilityId: string;
  planId: MembershipPlan["plan_id"];
  status: NonNullable<MembershipPlanUpdate["status"]>;
}

const PLAN_TYPE_OPTIONS = [
  "monthly",
  "quarterly",
  "annual",
  "visits_card",
] as const satisfies readonly MembershipPlanType[];

const PLAN_TYPE_LABEL: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
  visits_card: "Visits card",
};

const STATUS_COLOR: Record<string, "default" | "success"> = {
  active: "success",
  inactive: "default",
};

const ENTITLEMENT_SERVICE_CLASSES = "classes";
const ENTITLEMENT_BENEFIT_INCLUDED_SESSIONS = "included_sessions" as const;
const MONEY_INPUT_PATTERN = /^\d{1,8}(?:\.\d{1,2})?$/;

const DAY_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

const planFormSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required."),
    description: z.string(),
    plan_type: z.enum(PLAN_TYPE_OPTIONS),
    price: z.string().superRefine((value, context) => {
      if (!MONEY_INPUT_PATTERN.test(value.trim())) {
        context.addIssue({
          code: "custom",
          message: "Enter a price with no more than 2 decimal places.",
        });
      }
    }),
    visit_limit: z.string(),
    included_classes_per_period: z.string().superRefine((value, context) => {
      if (value.trim() === "") return;
      const amount = Number(value);
      if (!Number.isInteger(amount) || amount < 0) {
        context.addIssue({
          code: "custom",
          message: "Enter a whole number of 0 or more, or leave blank.",
        });
      }
    }),
    allowed_days: z.array(z.number().int().min(0).max(6)),
    allowed_time_start: z.string(),
    allowed_time_end: z.string(),
  })
  .superRefine((values, context) => {
    if (values.plan_type === "visits_card") {
      const visitLimit = values.visit_limit.trim();
      const amount = Number(visitLimit);
      if (!/^[1-9]\d*$/.test(visitLimit) || !Number.isSafeInteger(amount)) {
        context.addIssue({
          code: "custom",
          path: ["visit_limit"],
          message: "Enter a whole number of visits greater than 0.",
        });
      }
    }

    const start = values.allowed_time_start.trim();
    const end = values.allowed_time_end.trim();
    if ((start && !end) || (!start && end)) {
      context.addIssue({
        code: "custom",
        path: ["allowed_time_start"],
        message: "Set both a start and end time, or leave both blank.",
      });
    } else if (start && end && start >= end) {
      context.addIssue({
        code: "custom",
        path: ["allowed_time_start"],
        message: "Start time must be before end time.",
      });
    }
  });

type PlanFormValues = z.infer<typeof planFormSchema>;

function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  const words = value.replaceAll("_", " ").trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : "—";
}

function planTypeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return PLAN_TYPE_LABEL[value] ?? humanize(value);
}

function formatVisitLimit(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return "Unlimited";
  return `${value} visit${value === 1 ? "" : "s"}`;
}

function isIncludedClassesEntitlement(entitlement: {
  service: string;
  benefit_type: string;
}): boolean {
  return (
    entitlement.service === ENTITLEMENT_SERVICE_CLASSES &&
    entitlement.benefit_type === ENTITLEMENT_BENEFIT_INCLUDED_SESSIONS
  );
}

function includedClassesFromPlan(plan?: MembershipPlan): string {
  const entry = plan?.entitlements?.find(isIncludedClassesEntitlement);
  return entry ? String(entry.benefit_value) : "";
}

function otherEntitlementsFromPlan(
  plan?: MembershipPlan,
): MembershipEntitlementCreate[] {
  if (!plan?.entitlements) return [];
  return plan.entitlements
    .filter((entitlement) => !isIncludedClassesEntitlement(entitlement))
    .map((entitlement) => ({
      service: entitlement.service,
      benefit_type:
        entitlement.benefit_type as MembershipEntitlementCreate["benefit_type"],
      benefit_value: entitlement.benefit_value,
    }));
}

function formValuesFromPlan(plan?: MembershipPlan): PlanFormValues {
  const planType = PLAN_TYPE_OPTIONS.includes(
    plan?.plan_type as MembershipPlanType,
  )
    ? (plan?.plan_type as MembershipPlanType)
    : "monthly";

  return {
    name: plan?.name ?? "",
    description: plan?.description ?? "",
    plan_type: planType,
    price:
      plan?.price === null || plan?.price === undefined
        ? ""
        : String(plan.price),
    visit_limit:
      plan?.visit_limit === null || plan?.visit_limit === undefined
        ? ""
        : String(plan.visit_limit),
    included_classes_per_period: includedClassesFromPlan(plan),
    allowed_days: Array.isArray(plan?.allowed_days)
      ? [...plan.allowed_days].sort((left, right) => left - right)
      : [],
    allowed_time_start: plan?.allowed_time_start ?? "",
    allowed_time_end: plan?.allowed_time_end ?? "",
  };
}

function requestBodyFromForm(
  values: PlanFormValues,
  plan: MembershipPlan | null,
): MembershipPlanCreate {
  const includedClassesRaw = values.included_classes_per_period.trim();
  const includedClasses = includedClassesRaw ? Number(includedClassesRaw) : 0;
  const entitlements = otherEntitlementsFromPlan(plan ?? undefined);

  if (includedClasses > 0) {
    entitlements.push({
      service: ENTITLEMENT_SERVICE_CLASSES,
      benefit_type: ENTITLEMENT_BENEFIT_INCLUDED_SESSIONS,
      benefit_value: asMoney(String(includedClasses))!,
    });
  }

  const start = values.allowed_time_start.trim();
  const end = values.allowed_time_end.trim();

  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    plan_type: values.plan_type,
    price: asMoney(values.price.trim())!,
    visit_limit:
      values.plan_type === "visits_card" ? Number(values.visit_limit) : null,
    allowed_days:
      values.allowed_days.length > 0 ? [...values.allowed_days] : null,
    allowed_time_start: start || null,
    allowed_time_end: end || null,
    entitlements,
  };
}

function PlanDialog({
  open,
  plan,
  onClose,
  onSubmit,
}: {
  open: boolean;
  plan: MembershipPlan | null;
  onClose: () => void;
  onSubmit: (data: MembershipPlanCreate) => Promise<void>;
}) {
  const isEdit = plan !== null;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitInFlightRef = useRef(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PlanFormValues>({
    defaultValues: formValuesFromPlan(plan ?? undefined),
    resolver: zodResolver(planFormSchema),
  });
  const planType = useWatch({ control, name: "plan_type" });

  useEffect(() => {
    if (!open) return;
    reset(formValuesFromPlan(plan ?? undefined));
    setSubmitError(null);
  }, [open, plan, reset]);

  const submit = handleSubmit(async (values) => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setSubmitError(null);
    try {
      await onSubmit(requestBodyFromForm(values, plan));
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to save this plan.",
      );
    } finally {
      submitInFlightRef.current = false;
    }
  });

  const timeError =
    errors.allowed_time_start?.message ?? errors.allowed_time_end?.message;

  return (
    <Dialog
      aria-labelledby="membership-plan-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={isSubmitting ? undefined : onClose}
      open={open}
    >
      <Box component="form" noValidate onSubmit={submit}>
        <DialogTitle id="membership-plan-dialog-title">
          {isEdit ? `Edit ${plan.name}` : "New membership plan"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box aria-labelledby="plan-details-heading" component="section">
              <Typography
                component="h2"
                id="plan-details-heading"
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1.5 }}
              >
                Plan details
              </Typography>
              <Stack spacing={2}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoFocus
                      error={Boolean(errors.name)}
                      fullWidth
                      helperText={errors.name?.message}
                      label="Name"
                      required
                      size="small"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description (optional)"
                      minRows={2}
                      multiline
                      size="small"
                    />
                  )}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    control={control}
                    name="price"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(errors.price)}
                        fullWidth
                        helperText={errors.price?.message}
                        label="Price"
                        required
                        size="small"
                        slotProps={{ htmlInput: { inputMode: "decimal" } }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="plan_type"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Plan type"
                        select
                        size="small"
                      >
                        {PLAN_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {planTypeLabel(option)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box aria-labelledby="plan-benefits-heading" component="section">
              <Typography
                component="h2"
                id="plan-benefits-heading"
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1.5 }}
              >
                Benefits
              </Typography>
              <Stack spacing={2}>
                {planType === "visits_card" ? (
                  <Controller
                    control={control}
                    name="visit_limit"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(errors.visit_limit)}
                        fullWidth
                        helperText={
                          errors.visit_limit?.message ??
                          "Visits included per monthly billing period."
                        }
                        label="Number of visits"
                        required
                        size="small"
                        slotProps={{ htmlInput: { inputMode: "numeric" } }}
                      />
                    )}
                  />
                ) : null}
                <Controller
                  control={control}
                  name="included_classes_per_period"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={Boolean(errors.included_classes_per_period)}
                      fullWidth
                      helperText={
                        errors.included_classes_per_period?.message ??
                        "Class enrollments covered each billing period. Leave blank when members pay per class."
                      }
                      label="Included classes (optional)"
                      size="small"
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      type="number"
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            <Box aria-labelledby="plan-access-heading" component="section">
              <Typography
                component="h2"
                id="plan-access-heading"
                variant="subtitle1"
                sx={{ fontWeight: 700, mb: 1.5 }}
              >
                Access window
              </Typography>
              <Stack spacing={2}>
                <FormControl component="fieldset" fullWidth>
                  <Typography
                    component="legend"
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Allowed days (optional)
                  </Typography>
                  <Controller
                    control={control}
                    name="allowed_days"
                    render={({ field }) => (
                      <ToggleButtonGroup
                        {...field}
                        aria-label="Allowed days"
                        onChange={(_event, values: number[]) =>
                          field.onChange(
                            [...values].sort((left, right) => left - right),
                          )
                        }
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          flexWrap: "wrap",
                          gap: 0.75,
                          "& .MuiToggleButtonGroup-grouped": {
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            m: 0,
                          },
                        }}
                      >
                        {DAY_OPTIONS.map((day) => (
                          <ToggleButton
                            aria-label={day.label}
                            key={day.value}
                            value={day.value}
                          >
                            {day.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    )}
                  />
                  <FormHelperText>
                    Leave all days unselected to allow access any day.
                  </FormHelperText>
                </FormControl>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Controller
                    control={control}
                    name="allowed_time_start"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(timeError)}
                        fullWidth
                        label="Access start (optional)"
                        size="small"
                        slotProps={{
                          htmlInput: { step: 60 },
                          inputLabel: { shrink: true },
                        }}
                        type="time"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="allowed_time_end"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(timeError)}
                        fullWidth
                        label="Access end (optional)"
                        size="small"
                        slotProps={{
                          htmlInput: { step: 60 },
                          inputLabel: { shrink: true },
                        }}
                        type="time"
                      />
                    )}
                  />
                </Stack>
                <FormHelperText error={Boolean(timeError)}>
                  {timeError ??
                    "Leave both times blank to allow access at any time."}
                </FormHelperText>
              </Stack>
            </Box>

            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            size="small"
            type="submit"
            variant="contained"
          >
            {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function PlanStatus({ plan }: { plan: MembershipPlan }) {
  return (
    <Chip
      color={STATUS_COLOR[plan.status] ?? "default"}
      label={humanize(plan.status)}
      size="small"
      variant="outlined"
    />
  );
}

function EditPlanButton({
  plan,
  onClick,
}: {
  plan: MembershipPlan;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={`Edit plan ${plan.name}`}
      onClick={onClick}
      size="small"
      startIcon={<EditOutlined />}
      variant="outlined"
    >
      Edit
    </Button>
  );
}

function CompactPlanList({
  plans,
  onEdit,
  showAvailability,
  availabilityDisabled,
  onAvailabilityChange,
}: {
  plans: MembershipPlan[];
  onEdit: (plan: MembershipPlan) => void;
  showAvailability: boolean;
  availabilityDisabled: boolean;
  onAvailabilityChange: (event: MembershipPlanAvailabilityChange) => void;
}) {
  return (
    <List disablePadding>
      {plans.map((plan, index) => (
        <ListItem
          disableGutters
          divider={index < plans.length - 1}
          key={plan.plan_id}
          sx={{ display: "block", px: 2, py: 2 }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                {plan.description ? (
                  <Typography color="text.secondary" variant="body2">
                    {plan.description}
                  </Typography>
                ) : null}
              </Box>
              <PlanStatus plan={plan} />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
            >
              <Typography variant="body2">
                {formatMoney(plan.price, "USD")}
              </Typography>
              <Typography color="text.secondary" aria-hidden="true">
                ·
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {planTypeLabel(plan.plan_type)}
              </Typography>
              <Typography color="text.secondary" aria-hidden="true">
                ·
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {formatVisitLimit(plan.visit_limit)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              <EditPlanButton plan={plan} onClick={() => onEdit(plan)} />
              {showAvailability ? <MembershipPlanAvailabilityControl plan={plan} disabled={availabilityDisabled}
                onChange={onAvailabilityChange} /> : null}
            </Stack>
          </Stack>
        </ListItem>
      ))}
    </List>
  );
}

function DesktopPlanTable({
  plans,
  onEdit,
  showAvailability,
  availabilityDisabled,
  onAvailabilityChange,
}: {
  plans: MembershipPlan[];
  onEdit: (plan: MembershipPlan) => void;
  showAvailability: boolean;
  availabilityDisabled: boolean;
  onAvailabilityChange: (event: MembershipPlanAvailabilityChange) => void;
}) {
  return (
    <TableContainer>
      <Table aria-label="Membership plans">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Visits</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plans.map((plan) => (
            <TableRow key={plan.plan_id} hover>
              <TableCell>
                <Typography sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                {plan.description ? (
                  <Typography color="text.secondary" variant="body2">
                    {plan.description}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>{formatMoney(plan.price, "USD")}</TableCell>
              <TableCell>{planTypeLabel(plan.plan_type)}</TableCell>
              <TableCell>{formatVisitLimit(plan.visit_limit)}</TableCell>
              <TableCell>
                <PlanStatus plan={plan} />
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                  <EditPlanButton plan={plan} onClick={() => onEdit(plan)} />
                  {showAvailability ? <MembershipPlanAvailabilityControl plan={plan} disabled={availabilityDisabled}
                    onChange={onAvailabilityChange} /> : null}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function MembershipPlans({ facilityId }: { facilityId: string }) {
  const queryClient = useQueryClient();
  const authority = useMyFacilityActions(facilityId);
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("sm"));
  const currentFacilityIdRef = useRef(facilityId);
  currentFacilityIdRef.current = facilityId;
  const availabilityScope = useRef({ facilityId, epoch: 0 });
  if (availabilityScope.current.facilityId !== facilityId) {
    availabilityScope.current = { facilityId, epoch: availabilityScope.current.epoch + 1 };
  }
  const [createOpen, setCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<MembershipPlan | null>(null);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const availabilityInFlight = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      availabilityScope.current.epoch += 1;
    };
  }, []);
  useEffect(() => { setAvailabilityError(null); }, [facilityId]);

  const plansQuery = useQuery({
    queryKey: ["membership-plans", facilityId],
    queryFn: () => listMembershipPlans(facilityId),
    retry: false,
  });
  const createMutation = useMutation({
    mutationFn: ({
      targetFacilityId,
      body,
    }: {
      targetFacilityId: string;
      body: MembershipPlanCreate;
    }) => createMembershipPlan(targetFacilityId, body),
  });
  const updateMutation = useMutation({
    mutationFn: ({
      planId,
      body,
    }: {
      planId: string;
      body: MembershipPlanUpdate;
    }) => updateMembershipPlan(planId, body),
  });
  const availabilityMutation = useMutation({
    mutationFn: (event: MembershipPlanAvailabilityChange) => updateMembershipPlan(event.planId, { status: event.status }),
    retry: false,
  });
  const canPrice = authority.isReady && authority.actions.includes("facility:price");
  const availabilityReady = plansQuery.status === "success" && plansQuery.fetchStatus === "idle";
  const handleAvailabilityChange = async (event: MembershipPlanAvailabilityChange) => {
    if (!mounted.current || availabilityInFlight.current || event.facilityId !== currentFacilityIdRef.current) return;
    const planState = queryClient.getQueryState<MembershipPlan[]>(["membership-plans", event.facilityId]);
    const actionState = queryClient.getQueryState<{ actions: string[] }>(["facility-actions", event.facilityId]);
    const current = planState?.data?.find((plan) => plan.plan_id === event.planId && plan.facility_id === event.facilityId);
    if (planState?.status !== "success" || planState.fetchStatus !== "idle" || planState.isInvalidated
      || actionState?.status !== "success" || actionState.fetchStatus !== "idle" || actionState.isInvalidated
      || !actionState.data?.actions.includes("facility:price") || !current
      || (event.status === "inactive" ? current.status !== "active" : current.status !== "inactive")) return;
    availabilityInFlight.current = true;
    const epoch = availabilityScope.current.epoch;
    const isCurrentScope = () => mounted.current && availabilityScope.current.epoch === epoch
      && availabilityScope.current.facilityId === event.facilityId;
    setAvailabilityError(null);
    let accepted = false;
    try {
      await availabilityMutation.mutateAsync(event);
      accepted = true;
      await queryClient.invalidateQueries({
        queryKey: ["membership-plans", event.facilityId], exact: true, refetchType: "none",
      });
      if (!isCurrentScope()) return;
      await plansQuery.refetch({ throwOnError: true });
    } catch (error) {
      if (!isCurrentScope()) return;
      setAvailabilityError(accepted
        ? "Change saved, but membership plans could not be refreshed. Reload to confirm the current status."
        : error instanceof Error ? error.message : "New purchase availability could not be changed.");
    } finally {
      availabilityInFlight.current = false;
    }
  };

  const handleAvailabilityRefresh = async () => {
    const { facilityId: targetFacilityId, epoch } = availabilityScope.current;
    try {
      await plansQuery.refetch({ throwOnError: true });
      if (mounted.current && availabilityScope.current.epoch === epoch
        && availabilityScope.current.facilityId === targetFacilityId) setAvailabilityError(null);
    } catch {
      // Keep the existing read failure visible; retrying never repeats the write.
    }
  };

  const handleCreate = async (body: MembershipPlanCreate) => {
    const targetFacilityId = facilityId;
    await createMutation.mutateAsync({ targetFacilityId, body });
    if (currentFacilityIdRef.current !== targetFacilityId) return;
    setCreateOpen(false);
    await plansQuery.refetch();
  };

  const handleUpdate = async (body: MembershipPlanCreate) => {
    if (!editPlan) return;
    const targetFacilityId = facilityId;
    await updateMutation.mutateAsync({ planId: editPlan.plan_id, body });
    if (currentFacilityIdRef.current !== targetFacilityId) return;
    setEditPlan(null);
    await plansQuery.refetch();
  };

  if (plansQuery.isPending && plansQuery.data === undefined) {
    return (
      <Stack aria-label="Loading membership plans" spacing={2}>
        <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
          <Skeleton height={32} variant="rounded" width={112} />
        </Stack>
        <Paper sx={{ p: 2 }} variant="outlined">
          <Stack spacing={1.5}>
            <Skeleton height={28} />
            <Skeleton height={28} />
            <Skeleton height={28} />
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (plansQuery.isError && plansQuery.data === undefined) {
    const message =
      plansQuery.error instanceof Error
        ? plansQuery.error.message
        : "Failed to load membership plans.";
    return (
      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          <Alert severity="error">{message}</Alert>
          <Button
            onClick={() => void plansQuery.refetch()}
            size="small"
            variant="outlined"
          >
            Try again
          </Button>
        </Stack>
      </Paper>
    );
  }

  const plans = plansQuery.data ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          onClick={() => setCreateOpen(true)}
          size="small"
          startIcon={<AddRounded />}
          variant="contained"
        >
          New plan
        </Button>
      </Stack>

      {availabilityError || plansQuery.isError ? <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Alert severity="error">{availabilityError ??
          "Membership plans could not be refreshed. The last loaded plans are still shown."}</Alert>
        <Button size="small" variant="outlined" disabled={plansQuery.fetchStatus !== "idle"}
          onClick={() => { void handleAvailabilityRefresh(); }}>
          Reload membership plans
        </Button>
      </Stack> : null}
      {canPrice ? <Typography color="text.secondary" variant="body2">
        Pausing stops new purchases. Paid memberships continue.
      </Typography> : null}

      <Paper
        aria-label="Membership plans"
        component="section"
        variant="outlined"
        sx={{ overflow: "hidden" }}
      >
        {plans.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ px: 2, py: 6, textAlign: "center" }}
            variant="body2"
          >
            No membership plans yet — create your first plan.
          </Typography>
        ) : desktop ? (
          <DesktopPlanTable plans={plans} onEdit={setEditPlan} showAvailability={canPrice}
            availabilityDisabled={!availabilityReady || availabilityMutation.isPending}
            onAvailabilityChange={(event) => { void handleAvailabilityChange(event); }} />
        ) : (
          <CompactPlanList plans={plans} onEdit={setEditPlan} showAvailability={canPrice}
            availabilityDisabled={!availabilityReady || availabilityMutation.isPending}
            onAvailabilityChange={(event) => { void handleAvailabilityChange(event); }} />
        )}
      </Paper>

      <PlanDialog
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        open={createOpen}
        plan={null}
      />
      <PlanDialog
        onClose={() => setEditPlan(null)}
        onSubmit={handleUpdate}
        open={editPlan !== null}
        plan={editPlan}
      />
    </Stack>
  );
}
